import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack
} from '@mui/material';
import {
  Email as EmailIcon,
  Dashboard as DashboardIcon,
  Build as BuildIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon,
  PhoneAndroid as MobileIcon
} from '@mui/icons-material';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
  getWebPageSchema
} from '../utils/seoSchemas';

const About = () => {
  const organizationSchema = getOrganizationSchema();
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'About', url: 'https://www.go-ops.digital/about' }
  ]);
  const webPageSchema = getWebPageSchema({
    name: 'About Go Ops',
    description: 'Go Ops is a UK-based garage management software platform designed for independent garages.',
    url: 'https://www.go-ops.digital/about'
  });

  const features = [
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: '#ff6b35' }} />,
      title: 'AI Booking Intake',
      description: 'Automatically extract and process bookings from emails. AI reads customer messages and creates structured job requests.'
    },
    {
      icon: <DashboardIcon sx={{ fontSize: 40, color: '#ff6b35' }} />,
      title: 'Visual Job Board',
      description: 'Drag-and-drop job assignments with technician load tracking. See who is available at a glance.'
    },
    {
      icon: <BuildIcon sx={{ fontSize: 40, color: '#ff6b35' }} />,
      title: 'Vehicle Health Checks',
      description: 'Digital inspections with photo and video uploads. Technicians capture issues on-site with their phone.'
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 40, color: '#ff6b35' }} />,
      title: 'Secure Payments',
      description: 'Send payment links to customers. They approve only what matters—safety-critical items are highlighted.'
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 40, color: '#ff6b35' }} />,
      title: 'Automatic Invoicing',
      description: 'Generate invoices automatically with VAT, parts, and labour split. Stay compliant effortlessly.'
    },
    {
      icon: <MobileIcon sx={{ fontSize: 40, color: '#ff6b35' }} />,
      title: 'Mobile Friendly',
      description: 'Works on any device. Technicians can manage jobs, upload media, and complete checks from their phones.'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>About Go Ops | AI-Powered Garage Management for UK Garages</title>
        <meta
          name="description"
          content="Go Ops is a UK-based garage management software platform designed for independent garages. Built with AI, it automates booking intake, job tracking, vehicle health checks, payments, and invoicing."
        />
        <link rel="canonical" href="https://www.go-ops.digital/about" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="About Go Ops | AI-Powered Garage Management" />
        <meta
          property="og:description"
          content="Go Ops is a UK-based garage management software platform designed for independent garages. Built with AI at its core."
        />
        <meta property="og:url" content="https://www.go-ops.digital/about" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Go Ops | AI-Powered Garage Management" />
        <meta
          name="twitter:description"
          content="Go Ops is a UK-based garage management software platform designed for independent garages."
        />
        <meta name="twitter:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="text"
                component={RouterLink}
                to="/about"
                sx={{ color: 'white' }}
              >
                About
              </Button>
              <Button
                variant="text"
                component={RouterLink}
                to="/faq"
                sx={{ color: 'white' }}
              >
                FAQ
              </Button>
              <Button
                variant="text"
                component={RouterLink}
                to="/contact"
                sx={{ color: 'white' }}
              >
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 3 }}>
            About Go Ops
          </Typography>
          {/* Entity clarity paragraph - critical for AI understanding */}
          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: 1.8, opacity: 0.95 }}>
            Go Ops is a UK-based garage management software platform designed for independent garages.
            Built with AI at its core, Go Ops automates booking intake, job tracking, vehicle health checks,
            payments, and invoicing—helping garage owners run their business more efficiently.
          </Typography>
        </Container>
      </Box>

      {/* Mission Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" component="h2" sx={{ fontSize: '2rem', fontWeight: 'bold', mb: 3 }}>
              Our Mission
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555', mb: 2 }}>
              Independent garages are the backbone of UK automotive care. Yet many still rely on
              paper-based systems, scattered emails, and manual processes that waste time and create errors.
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}>
              Go Ops was built to change that. We combine AI-powered automation with an intuitive
              interface that any garage team can use—no technical expertise required. From the moment
              a booking email arrives to the final invoice, every step is streamlined.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#2c3e50',
                borderRadius: 2,
                p: 4,
                color: 'white'
              }}
            >
              <Typography variant="h3" component="h3" sx={{ fontSize: '1.5rem', fontWeight: 'bold', mb: 3 }}>
                Why UK Garages Choose Go Ops
              </Typography>
              <Stack spacing={2}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  ✓ Built specifically for UK independent garages
                </Typography>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  ✓ AI extracts bookings from emails automatically
                </Typography>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  ✓ Digital vehicle health checks with media
                </Typography>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  ✓ VAT-compliant invoicing
                </Typography>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  ✓ Secure customer payment links
                </Typography>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  ✓ Works on mobile devices
                </Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Features Grid */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            sx={{ fontSize: '2rem', fontWeight: 'bold', mb: 6, textAlign: 'center' }}
          >
            What Go Ops Does
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h3" component="h3" sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{ color: '#666', lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#ff6b35', color: 'white', py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" component="h2" sx={{ fontSize: '2rem', fontWeight: 'bold', mb: 2 }}>
            Ready to Transform Your Garage?
          </Typography>
          <Typography sx={{ fontSize: '1.1rem', mb: 4, opacity: 0.95 }}>
            Join UK garages already using Go Ops to streamline their operations.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact"
              size="large"
              sx={{ bgcolor: 'white', color: '#ff6b35', '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              Get a Demo
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/faq"
              size="large"
              sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              View FAQ
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/about" sx={{ color: 'white', textTransform: 'none' }}>
                About
              </Button>
              <Button component={RouterLink} to="/faq" sx={{ color: 'white', textTransform: 'none' }}>
                FAQ
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
